import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Loader2, ShieldCheck, MessageCircle, CheckCircle2, Copy, RefreshCw, ArrowRight, Hourglass, Crown } from "lucide-react";
import { toast } from "sonner";

const TRIAL_LINE = "PKR 1,500/month ya PKR 15,000/year — koi hidden fee nahi.";

export default function AuthModal({ open, onOpenChange, mode: initialMode = "signup", onSuccess }) {
  const { login, register, formatApiError } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [busy, setBusy] = useState(false);

  // Referral state
  const [refInput, setRefInput] = useState("");        // user-typed code (manual entry from Play Store install)
  const [refLookupBusy, setRefLookupBusy] = useState(false);
  const [refInfo, setRefInfo] = useState(null); // {code, partner_first_name} | {code, invalid:true} | null

  // Signup state
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [signupPwd, setSignupPwd] = useState("");

  // Login state
  const [identifier, setIdentifier] = useState("");
  const [loginPwd, setLoginPwd] = useState("");

  // Verification step
  const [verifyInfo, setVerifyInfo] = useState(null); // {code, wa_link, ...}
  const [pendingCreds, setPendingCreds] = useState(null); // {mobile|email, password}
  const [foundingStats, setFoundingStats] = useState(null);

  useEffect(() => {
    if (mode === "signup" && !foundingStats) {
      api.get("/platform/founding-stats")
        .then((r) => setFoundingStats(r.data))
        .catch(() => {});
    }
    // Auto-detect ?ref=CODE from URL on web signup (no-op inside Android APK).
    if (mode === "signup" && !refInfo) {
      const params = new URLSearchParams(window.location.search);
      const code = (params.get("ref") || "").toUpperCase().trim();
      if (code) {
        setRefInput(code);
        api.get(`/referrals/lookup/${encodeURIComponent(code)}`)
          .then((r) => setRefInfo(r.data))
          .catch(() => setRefInfo({ code, partner_first_name: null, invalid: true }));
      }
    }
  }, [mode, foundingStats, refInfo]);

  // Validate a manually-typed referral code (called on blur / after debounce).
  const validateRefCode = async (raw) => {
    const code = (raw || "").toUpperCase().trim();
    if (!code) { setRefInfo(null); return; }
    setRefLookupBusy(true);
    try {
      const { data } = await api.get(`/referrals/lookup/${encodeURIComponent(code)}`);
      setRefInfo(data);
    } catch (_) {
      setRefInfo({ code, partner_first_name: null, invalid: true });
    } finally {
      setRefLookupBusy(false);
    }
  };

  useEffect(() => {
    setMode(initialMode);
    if (!open) {
      setVerifyInfo(null);
      setPendingCreds(null);
    }
  }, [initialMode, open]);

  const submitSignup = async (e) => {
    e.preventDefault();
    if (!/^\d{10,12}$/.test(mobile.replace(/\D/g, ""))) {
      toast.error("Sahi mobile number daalain");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Sahi email address daalain");
      return;
    }
    if (signupPwd.length < 6) {
      toast.error("Password minimum 6 characters chahiye");
      return;
    }
    // If user typed a code but hasn't validated, or validated as invalid — block.
    const typedCode = (refInput || "").toUpperCase().trim();
    if (typedCode) {
      if (refInfo?.invalid || (refInfo && refInfo.code !== typedCode)) {
        toast.error("Referral code galat hai. Sahi code daalain ya khali chhorein.");
        return;
      }
      if (!refInfo) {
        // Ensure last-typed value is validated before we send.
        await validateRefCode(typedCode);
      }
    }
    setBusy(true);
    try {
      const v = await register({
        email: email.trim().toLowerCase(),
        mobile: mobile.trim(),
        password: signupPwd,
        shop_name: businessName.trim(),
        owner_name: name.trim(),
        referral_code: refInfo && !refInfo.invalid ? refInfo.code : undefined,
      });
      // Store creds for auto-login after verification
      setPendingCreds({ email: email.trim().toLowerCase(), password: signupPwd });
      setVerifyInfo(v);
      setMode("verify");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const isEmail = identifier.includes("@");
      const payload = isEmail
        ? { email: identifier.trim(), password: loginPwd }
        : { mobile: identifier.trim(), password: loginPwd };
      const u = await login(payload);
      toast.success("Welcome back!");
      onOpenChange(false);
      onSuccess?.(u);
    } catch (err) {
      // Verification-pending? Move to verify step
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "verification_pending" && detail.verification?.code) {
        setVerifyInfo({
          code: detail.verification.code,
          wa_link: detail.verification.wa_link,
          verify_message: `Hello HisaabPro, my verification code is ${detail.verification.code}`,
          support_display: "+92 371 8067478",
          business_name: identifier,
        });
        const isEmail2 = identifier.includes("@");
        setPendingCreds(isEmail2 ? { email: identifier.trim(), password: loginPwd } : { mobile: identifier.trim(), password: loginPwd });
        setMode("verify");
        toast.info("WhatsApp verification pending. Send the code se WhatsApp pe.");
      } else {
        toast.error(formatApiError(err) || "Login failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onVerified = async () => {
    if (!pendingCreds) {
      toast.success("Verified! Please login now.");
      onOpenChange(false);
      return;
    }
    setBusy(true);
    try {
      const u = await login(pendingCreds);
      toast.success("Welcome to HisaabPro 🎉");
      onOpenChange(false);
      onSuccess?.(u);
    } catch (err) {
      toast.error(formatApiError(err) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-y-auto max-h-[calc(100dvh-2rem)]" data-testid={`auth-modal-${mode}`}>
        <div className="bg-emerald-600 text-white px-6 py-5">
          <div className="flex items-center gap-2 text-emerald-100 text-xs uppercase font-bold tracking-widest">
            <img src="/brand/logo.svg" alt="" width="16" height="16" className="select-none" /> HisaabPro
          </div>
          <DialogHeader className="mt-1">
            <DialogTitle className="text-2xl font-display font-bold text-white">
              {mode === "signup" && "Create your account"}
              {mode === "login" && "Welcome back"}
              {mode === "verify" && "Verify your WhatsApp"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-emerald-50 text-sm mt-1">
            {mode === "signup" && "Apni shop ka pehla account banayein. Sirf 30 second."}
            {mode === "login" && "Mobile / email aur password se login karein."}
            {mode === "verify" && "Ek WhatsApp message bhejein — phir aap ka account activate ho jayega."}
          </p>
        </div>

        <div className="px-6 py-5">
          {mode === "signup" && (
            <form onSubmit={submitSignup} className="space-y-3.5" data-testid="auth-signup-form">
              {foundingStats && foundingStats.remaining > 0 && (
                <div
                  className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-300 px-3 py-2.5 text-amber-900"
                  data-testid="auth-founding-banner"
                >
                  <Crown className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <div className="font-bold">🏆 Founding Member offer</div>
                    <div>
                      <span className="font-bold">{foundingStats.remaining} / {foundingStats.total_spots}</span> spots left — get PKR 2,999/quarter lifetime lock-in.
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Aap ka naam</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} placeholder="Ali Khan" data-testid="auth-su-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Mobile number</Label>
                <Input value={mobile} onChange={(e) => setMobile(e.target.value)} required placeholder="03001234567" inputMode="numeric" data-testid="auth-su-mobile" />
              </div>
              <div className="space-y-1.5">
                <Label>Email address</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@yourbusiness.com"
                  inputMode="email"
                  autoComplete="email"
                  data-testid="auth-su-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Business / shop name</Label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} required placeholder="Al-Madina Mobiles" data-testid="auth-su-shop" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input type="password" value={signupPwd} onChange={(e) => setSignupPwd(e.target.value)} required minLength={6} placeholder="Min 6 characters" data-testid="auth-su-password" />
              </div>

              {/* Referral Code — optional. Auto-filled from ?ref=CODE (web) or entered
                  manually by Play Store installs. Validated against the backend. */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  Referral code <span className="text-[10px] text-slate-500 font-normal">(optional)</span>
                </Label>
                <Input
                  value={refInput}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setRefInput(v);
                    // If user is typing, clear old lookup so they don't see a stale chip
                    if (refInfo && refInfo.code !== v) setRefInfo(null);
                  }}
                  onBlur={() => validateRefCode(refInput)}
                  placeholder="e.g. AHMED-1234"
                  className="font-mono"
                  autoCapitalize="characters"
                  data-testid="auth-su-referral-code"
                />
                {refLookupBusy && (
                  <div className="text-[11px] text-slate-500 flex items-center gap-1" data-testid="auth-referral-checking">
                    <Loader2 className="w-3 h-3 animate-spin" /> Checking code…
                  </div>
                )}
                {!refLookupBusy && refInfo && !refInfo.invalid && (
                  <div
                    className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-300 px-2.5 py-1.5 text-emerald-900 text-xs"
                    data-testid="auth-referral-chip"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <div>Referred by <b>{refInfo.partner_first_name}</b> · code <b className="font-mono">{refInfo.code}</b></div>
                  </div>
                )}
                {!refLookupBusy && refInfo && refInfo.invalid && (
                  <div className="text-[11px] text-red-700" data-testid="auth-referral-invalid">
                    Ye referral code exist nahi karta. Check karke dubara likhein ya khali chhorein.
                  </div>
                )}
              </div>

              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-md px-3 py-2 font-medium">
                {TRIAL_LINE}
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-base h-11" data-testid="auth-su-submit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Continue → Verify on WhatsApp
              </Button>
              <div className="text-center text-sm text-slate-600 pt-1">
                Already have account?{" "}
                <button type="button" onClick={() => setMode("login")} className="text-emerald-700 font-bold hover:underline" data-testid="auth-switch-login">
                  Login
                </button>
              </div>
            </form>
          )}

          {mode === "login" && (
            <form onSubmit={submitLogin} className="space-y-3.5" data-testid="auth-login-form">
              <div className="space-y-1.5">
                <Label>Mobile / email</Label>
                <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} required placeholder="03001234567 or you@email.com" data-testid="auth-li-id" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input type="password" value={loginPwd} onChange={(e) => setLoginPwd(e.target.value)} required data-testid="auth-li-password" />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-base h-11" data-testid="auth-li-submit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Login
              </Button>
              <div className="text-center text-sm text-slate-600 pt-1">
                Don&apos;t have account?{" "}
                <button type="button" onClick={() => setMode("signup")} className="text-emerald-700 font-bold hover:underline" data-testid="auth-switch-signup">
                  Sign up
                </button>
              </div>
            </form>
          )}

          {mode === "verify" && verifyInfo && (
            <VerifyStep
              verifyInfo={verifyInfo}
              setVerifyInfo={setVerifyInfo}
              onVerified={onVerified}
              onBack={() => setMode(pendingCreds ? "login" : "signup")}
              busy={busy}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------
// VerifyStep — show code, deep-link button, status polling
// ------------------------------------------------------------
function VerifyStep({ verifyInfo, setVerifyInfo, onVerified, onBack, busy }) {
  const [polling, setPolling] = useState(true);
  const [resending, setResending] = useState(false);
  const intervalRef = useRef(null);
  const v = verifyInfo;

  useEffect(() => {
    if (!polling) return;
    intervalRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/verifications/status?code=${encodeURIComponent(v.code)}`);
        if (data.status !== v.status) {
          setVerifyInfo({ ...v, ...data });
        }
        if (data.status === "verified") {
          setPolling(false);
          clearInterval(intervalRef.current);
        }
      } catch (err) {
        // Polling loop — surface transient errors in console without spamming
        // the user with toasts; the next poll will retry.
        console.warn("[verify-poll] status check failed:", err?.message || err);
      }
    }, 4000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.code, polling]);

  const isVerified = v.status === "verified";
  const isExpired = v.status === "expired" || v.status === "rejected";

  const copyCode = () => {
    navigator.clipboard.writeText(v.code);
    toast.success("Code copied");
  };

  const resend = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/verifications/resend`, { code: v.code });
      setVerifyInfo({ ...v, ...data });
      toast.success("Code refreshed — try sending again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  if (isVerified) {
    return (
      <div className="text-center space-y-4 py-2" data-testid="verify-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <div>
          <div className="font-display font-bold text-2xl text-slate-900">Welcome to HisaabPro 🎉</div>
          <div className="text-sm text-slate-600 mt-1">Aap ka WhatsApp number verify ho gaya hai. Ab subscription pay kar ke sab features unlock karein.</div>
        </div>
        <Button
          onClick={onVerified}
          disabled={busy}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 text-base"
          data-testid="verify-go-to-dashboard"
        >
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Go to Dashboard <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="verify-step">
      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-center">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your verification code</div>
        <div className="font-mono font-bold text-3xl text-slate-900 tabular tracking-wider mt-1" data-testid="verify-code">
          {v.code}
        </div>
        <button
          type="button"
          onClick={copyCode}
          className="text-xs text-emerald-700 font-semibold hover:underline mt-1 inline-flex items-center gap-1"
          data-testid="verify-copy-code"
        >
          <Copy className="w-3 h-3" /> Copy code
        </button>
      </div>

      <ol className="space-y-2 text-sm text-slate-700">
        <li className="flex gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">1</span>
          <span>Neeche button par click karein — WhatsApp app khulega.</span>
        </li>
        <li className="flex gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">2</span>
          <span>Pehle se likha hua message <b>send</b> karein.</span>
        </li>
        <li className="flex gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">3</span>
          <span>Hum WhatsApp pe message dekh ke account approve karein ge.</span>
        </li>
      </ol>

      <a
        href={v.wa_link}
        target="_blank"
        rel="noreferrer"
        data-testid="verify-open-wa"
        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-base font-bold h-11 transition shadow-md shadow-emerald-500/30"
      >
        <MessageCircle className="w-5 h-5" fill="white" strokeWidth={1.5} />
        Send Verification Code on WhatsApp ({v.support_display})
      </a>

      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="verify-poll-status">
        <Hourglass className={`w-3.5 h-3.5 ${polling ? "animate-pulse" : ""}`} />
        {isExpired ? (
          <span><b>Code {v.status}</b> — please refresh.</span>
        ) : (
          <span>Waiting for admin approval… (auto-refreshes every 4 sec)</span>
        )}
      </div>

      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={resend}
          disabled={resending}
          className="text-slate-600 hover:text-slate-900 font-semibold inline-flex items-center gap-1"
          data-testid="verify-resend"
        >
          <RefreshCw className={`w-3 h-3 ${resending ? "animate-spin" : ""}`} /> Refresh code
        </button>
        <button type="button" onClick={onBack} className="text-slate-500 hover:text-slate-800" data-testid="verify-back">
          ← Back
        </button>
      </div>
    </div>
  );
}
